import { useQuery } from "@tanstack/react-query";
import { getDocumentsApi } from "../api.service";
import type { User } from "@/core/types/auth";
import type { DocumentPropsTypes, DocumentApiResponse } from "@/core/types/documents";

type DocumentsApiResponseData = {
    documents: DocumentApiResponse[];
};

/**
 * Maps API document response to application document type
 * Transforms docDocType to docType and handles field mappings
 */
const mapApiDocumentToDocumentType = (apiDoc: DocumentApiResponse): DocumentPropsTypes => {
    return {
        docType: apiDoc.docDocType,
        docNo: apiDoc.docNo,
        formName: apiDoc.formName,
        formPage: apiDoc.formPage,
        url: apiDoc.url || undefined,
        permissions: {
            view: apiDoc.permissions.view,
            add: apiDoc.permissions.add,
            edit: apiDoc.permissions.edit,
            delete: apiDoc.permissions.delete,
            assign: false, // Not in API response, default to false
            approve: apiDoc.permissions.approve,
        },
        approve: apiDoc.permissions.approve,
        prevDocType: apiDoc.prevDocType || undefined,
        nextDocType: apiDoc.nextDocType || undefined,
        isFollowUp: apiDoc.isFollowUp,
        refDoctype: apiDoc.refDoctype,
        status: apiDoc.status,
        tableName: apiDoc.tableName || undefined,
        tablePrefix: apiDoc.tablePrefix || undefined,
        dropDownFnName: apiDoc.dropDownFnName || undefined,
        detailTables: apiDoc.detailTables,
    };
};

/**
 * Extracts documents array from API response
 * Handles both direct array and nested object structures
 */
const extractDocumentsFromResponse = (
    data: DocumentsApiResponseData | DocumentApiResponse[] | unknown
): DocumentApiResponse[] => {
    if (Array.isArray(data)) {
        return data;
    }

    if (data && typeof data === 'object' && 'documents' in data) {
        const responseData = data as DocumentsApiResponseData;
        if (Array.isArray(responseData.documents)) {
            return responseData.documents;
        }
    }

    return [];
};

/**
 * React Query hook to fetch and transform documents list
 * @param user - Current user object
 * @param isAuthenticated - Whether user is authenticated (defaults to true if not provided)
 * @returns Query result with mapped DocumentPropsTypes[]
 */
export const useDocumentsList = (user: User | null, isAuthenticated?: boolean) => {
    const userId = user?.docNo;
    const isEnabled = Boolean(userId && user && (isAuthenticated !== false));
    
    return useQuery<DocumentPropsTypes[]>({
        queryKey: ['documents', userId],
        queryFn: async () => {
            if (!userId) {
                throw new Error('User ID is required to fetch documents');
            }

            const response = await getDocumentsApi();
            
            if (!response?.data) {
                return [];
            }

            const apiDocuments = extractDocumentsFromResponse(response.data);
            return apiDocuments.map(mapApiDocumentToDocumentType);
        },
        enabled: isEnabled,
        retry: 1,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};